// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClientOrderService, { ClientSampleRequest } from '../../service/clientOrder.service';

export default function ReqSamplesPage() {
  const [samples, setSamples] = useState<ClientSampleRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [selectedSample, setSelectedSample] = useState<ClientSampleRequest | null>(null);

  const fetchSamples = async () => {
    setLoading(true);
    try {
      const data = await ClientOrderService.getReqSamples();
      setSamples(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSamples();
  }, []);

  const filtered = samples.filter((smp) =>
    smp.requestCode.toLowerCase().includes(search.toLowerCase()) ||
    smp.subject.toLowerCase().includes(search.toLowerCase()) ||
    smp.productName.toLowerCase().includes(search.toLowerCase()) ||
    smp.contactName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Inventory2Icon sx={{ color: '#A77B58', fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Follow-Ups • REQ-samples
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#756B63', mt: 0.5 }}>
            Sub-menu managing physical product sample dispatches, courier tracking, and test specimens
          </Typography>
        </Box>

        <Chip
          label={`${samples.length} Sample Requests`}
          sx={{ bgcolor: '#FAF6F0', color: '#5F4B3B', fontWeight: 700, border: '1px solid #E8DFD5' }}
        />
      </Box>

      {/* Search Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E8DFD5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search sample requests by code, subject, product, recipient..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#A77B58' }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={fetchSamples}
          startIcon={<RefreshIcon />}
          sx={{
            borderColor: '#D6CEC4',
            color: '#5F4B3B',
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          Refresh
        </Button>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E8DFD5', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: '#FAF6F0' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Request ID</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Subject & Sample Details</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Sample Quantity</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Shipment Freight Term</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px', textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: '#A77B58' }} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: '#756B63' }}>No sample requests found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: '13px' }}>
                    {item.requestCode}
                    <Typography sx={{ fontSize: '11px', color: '#9E948C' }}>{item.createdAt}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027' }}>
                      {item.subject}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                      Product: {item.productName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#A77B58' }}>
                      {item.samples} {item.sampleUnit}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '12.5px', color: '#5F4B3B' }}>
                    {item.shipmentPay}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: item.status.includes('Dispatched') ? '#eff6ff' : '#ecfdf5',
                        color: item.status.includes('Dispatched') ? '#1d4ed8' : '#059669',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedSample(item)}
                      startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        borderColor: '#D6CEC4',
                        color: '#5F4B3B',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '11.5px',
                        '&:hover': { borderColor: '#A77B58', bgcolor: 'rgba(167, 123, 88, 0.05)' },
                      }}
                    >
                      View Request
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sample Detail Dialog */}
      <Dialog
        open={Boolean(selectedSample)}
        onClose={() => setSelectedSample(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {selectedSample && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedSample.requestCode}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>Date: {selectedSample.createdAt}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedSample(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#3B3027', mb: 1.5 }}>
                {selectedSample.subject}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#FAF6F0', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                  <strong>Product:</strong> {selectedSample.productName}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Samples:</strong> {selectedSample.samples} ({selectedSample.sampleUnit})
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Courier Freight Terms:</strong> {selectedSample.shipmentPay}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Contact Person:</strong> {selectedSample.contactName} ({selectedSample.businessContact})
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 0.5 }}>Special Instructions:</Typography>
              <Typography sx={{ fontSize: '13px', color: '#5F4B3B', lineHeight: 1.6 }}>
                {selectedSample.message}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSelectedSample(null)} sx={{ textTransform: 'none' }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
